/* eslint-disable max-lines-per-function */
// @ts-nocheck
import React from 'react';
import InteractiveSlider from '../InteractiveSlider';
import { CarComparisonData } from '../../types/carComparison';
import { calculateMonthlyPayment, getTaxAmount } from '../../utils/carCalculations';
import { renderInputSection } from '../../utils/renderInputSection';

interface FinanceInputsProps {
  data: CarComparisonData;
  onInputChange: (field: keyof CarComparisonData, value: any) => void;
  showGuidance: (title: string, content: string) => void;
  formatCurrency: (value: number) => string;
  includeFinanceScenario: boolean;
  setIncludeFinanceScenario: (include: boolean) => void;
}

const FinanceInputs: React.FC<FinanceInputsProps> = ({
  data,
  onInputChange,
  showGuidance,
  formatCurrency,
  includeFinanceScenario,
  setIncludeFinanceScenario
}) => {

  return (
    renderInputSection('Finance Scenario', (
      <>
        <div className='interactive-slider'>
          <div className="label-with-guidance" style={{ "display": "block", "textAlign": "center" }}>
            <input
              type="checkbox"
              checked={includeFinanceScenario}
              onChange={(e) => setIncludeFinanceScenario(e.target.checked)}
              style={{ width: '20px', height: '20px', cursor: 'pointer' }}
            />
            <br />
            <span style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
              Compare Financing?
            </span>
            <br />
            <small style={{ display: 'block', marginLeft: '28px', color: '#666', marginTop: '0.25rem' }}>
              Consider financing? Include this if you know want to know how much this costs!
            </small>
          </div>
        </div>
        {includeFinanceScenario && (<>
          <InteractiveSlider
            config={{
              min: 0,
              max: 200000,
              step: 1000,
              defaultValue: 5000,
              format: 'currency',
              label: 'Down Payment'
            }}
            value={data.downPayment}
            onChange={(value) => onInputChange('downPayment', value)}
            guidanceTitle="Down Payment"
            guidanceContent={"What's your down payment?<br><br>" +
              '<strong>Typical down payments:</strong><br>' +
              '• 10%: Minimum for most loans<br>' +
              '• 20%: Sweet spot - avoids PMI, better rates<br>' +
              '• 30%+: Excellent rates, lower monthly payments<br><br>' +
              '<strong>Benefits of larger down payments:</strong><br>' +
              '• Lower monthly payments<br>' +
              '• Better interest rates<br>' +
              '• Less total interest paid<br>' +
              '• Avoid negative equity<br><br>' +
              'But remember - that money could be invested instead!'
            }
            onShowGuidance={showGuidance}
          />

          <InteractiveSlider
            config={{
              min: 0,
              max: 15,
              step: 0.1,
              defaultValue: 4.5,
              format: 'percent',
              label: 'Interest Rate (%)'
            }}
            value={data.interestRate}
            onChange={(value) => onInputChange('interestRate', value)}
            guidanceTitle="Interest Rate"
            guidanceContent={
              "What's your loan interest rate?<br><br>" +
              '<strong>Current rates (2024):</strong><br>' +
              '• Excellent credit (750+): 3-5%<br>' +
              '• Good credit (700-749): 5-7%<br>' +
              '• Fair credit (650-699): 7-10%<br>' +
              '• Poor credit (below 650): 10-15%+<br><br>' +
              '<strong>Tips for better rates:</strong><br>' +
              '• Shop around with multiple lenders<br>' +
              '• Consider credit union rates<br>' +
              '• Larger down payments help<br>' +
              '• Shorter loan terms = lower rates<br><br>' +
              'Check your credit score first - it makes a huge difference!'
            }
            onShowGuidance={showGuidance}
          />


          <InteractiveSlider
            config={{
              min: 1,
              max: 10,
              step: 1,
              defaultValue: 5,
              format: 'number',
              label: 'Financing Years'
            }}
            value={data.financingYears}
            onChange={(value) => onInputChange('financingYears', value)}
          />

          <InteractiveSlider
            config={{
              min: 0,
              max: 5000,
              step: 50,
              defaultValue: 0,
              format: 'currency',
              label: 'Finance Fees'
            }}
            value={data.financeFees}
            onChange={(value) => onInputChange('financeFees', value)}
            guidanceTitle="Finance Fees"
            guidanceContent={
              '<strong>⚠️ ONLY applies to the "Finance" scenario - NOT Buy (cash) or Lease</strong><br><br>' +
              "Fees charged by the lender or dealer to initiate financing:<br><br>" +
              '<strong>Common finance fees:</strong><br>' +
              '• Loan origination fee ($200-500)<br>' +
              '• Processing/documentation fee ($100-300)<br>' +
              '• Credit check fee ($25-50)<br>' +
              '• Title/lien fee ($50-150)<br><br>' +
              '<strong>Note:</strong> Tax is applied to these fees based on your tax rate setting.<br><br>' +
              '<strong>Tip:</strong> These fees are often negotiable - ask your lender to waive or reduce them!'
            }
            onShowGuidance={showGuidance}
          />

          <InteractiveSlider
            config={{
              min: 0,
              max: 2000,
              step: 50,
              defaultValue: 0,
              format: 'currency',
              label: 'Extra Monthly Payment'
            }}
            value={data.extraMonthlyPayment}
            onChange={(value) => onInputChange('extraMonthlyPayment', value)}
            guidanceTitle="Extra Monthly Payment"
            guidanceContent={
              "Pay extra each month to pay off your loan faster!<br><br>" +
              '<strong>Benefits of extra payments:</strong><br>' +
              '• Pay off loan years earlier<br>' +
              '• Save thousands in interest<br>' +
              '• Build equity faster<br>' +
              '• Own your car sooner<br><br>' +
              '<strong>Example:</strong><br>' +
              'On a $30k loan at 5% for 5 years:<br>' +
              '• Regular payment: $566/month<br>' +
              '• Pay extra $200/month → save $1,800 in interest!<br>' +
              '• Pay off in 3.5 years instead of 5<br><br>' +
              'Even small extra payments make a big difference!'
            }
            onShowGuidance={showGuidance}
          />

          {/* Calculated Monthly Payment Display */}
          <div className="calculated-value">
            <strong>Base Monthly Payment: {formatCurrency(calculateMonthlyPayment(data))}</strong>
            {data.extraMonthlyPayment > 0 && (
              <>
                <br />
                <strong style={{ color: '#22c55e' }}>
                  Total Payment (with extra): {formatCurrency(calculateMonthlyPayment(data) + data.extraMonthlyPayment)}
                </strong>
              </>
            )}
            <br />
            <small>Loan Amount: {formatCurrency(data.totalCost + getTaxAmount(data) + data.fees - data.downPayment)}</small>
          </div>
        </>)}
      </>
    ))
  );
};

export default FinanceInputs;